/**
 * Owner: Masa Bayu
 * File: ReaderProfileLibrary.tsx
 * Date:Time: 2026-10-18 23:18
 * Description: §393 — seksyen Perpustakaan Profil Reader: grid rak (cover
 *   nyata + progress + CTA), grid selesai, senarai wishlist. Semua item
 *   nampak serentak (tiada skrol tersembunyi).
 */

import type { ReactElement } from 'react';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import type { LibraryShelfCard } from '../library/shared/library-types';
import type { ReaderProfileData } from './reader-profile-data';
import type { ReaderProfileLabels } from './reader-profile-labels';

function BookGrid({
  cards,
  labels,
  onNavigate,
}: {
  readonly cards: readonly LibraryShelfCard[];
  readonly labels: ReaderProfileLabels;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <ul className="qxr__grid">
      {cards.map((card) => (
        <li key={card.item.libraryItemId} className="qxr__book">
          {card.coverUrl !== null ? (
            <img className="qxr__cover" src={card.coverUrl} alt="" loading="lazy" />
          ) : (
            <span className="qxr__cover qxr__cover--none" aria-hidden="true">
              <LandingIcon name="book" size={22} />
            </span>
          )}
          <strong className="qxr__booktitle">{card.item.title}</strong>
          <span className="qxr__muted">{card.progressLabel}</span>
          {card.item.offlineAvailable ? (
            <span className="qxr__chip">
              <LandingIcon name="download" size={13} /> {labels.offlineReady}
            </span>
          ) : null}
          <div
            className="qxr__bar"
            role="progressbar"
            aria-valuenow={card.item.progress.percentComplete}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={card.progressLabel}
          >
            <span style={{ width: `${String(card.item.progress.percentComplete)}%` }} />
          </div>
          {card.canOpen ? (
            <button
              type="button"
              className="qxr__cta"
              onClick={() => onNavigate(`/read/${encodeURIComponent(card.item.publicationId)}`)}
            >
              {labels.openReader}
            </button>
          ) : (
            <span className="qxr__muted">{card.accessLabel}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ReaderProfileLibrary({
  data,
  labels,
  onNavigate,
}: {
  readonly data: ReaderProfileData;
  readonly labels: ReaderProfileLabels;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  const isEmpty = data.shelf.length === 0 && data.wishlist.length === 0;
  return (
    <div className="qxr__stack">
      <section className="qxr__card" aria-labelledby="qxr-shelf">
        <h2 id="qxr-shelf" className="qxr__section-title">
          <LandingIcon name="book" size={18} /> {labels.shelfTitle}
        </h2>
        {data.shelf.length === 0 ? (
          <p className="qxr__empty">{labels.emptyLibrary}</p>
        ) : (
          <BookGrid cards={data.shelf} labels={labels} onNavigate={onNavigate} />
        )}
      </section>

      {data.completed.length > 0 ? (
        <section className="qxr__card" aria-labelledby="qxr-done">
          <h2 id="qxr-done" className="qxr__section-title">
            <LandingIcon name="checkCircle" size={18} /> {labels.completedTitle}
          </h2>
          <BookGrid cards={data.completed} labels={labels} onNavigate={onNavigate} />
        </section>
      ) : null}

      <section className="qxr__card" aria-labelledby="qxr-wish">
        <h2 id="qxr-wish" className="qxr__section-title">
          <LandingIcon name="heart" size={18} /> {labels.wishlistTitle}
        </h2>
        {data.wishlist.length === 0 ? (
          <p className="qxr__empty">{labels.emptyLibrary}</p>
        ) : (
          <ul className="qxr__rows">
            {data.wishlist.map((w) => (
              <li key={w.wishlistItemId}>
                <span className="qxr__rowtitle">{w.title}</span>
                <button
                  type="button"
                  className="qxr__cta qxr__cta--ghost"
                  onClick={() => onNavigate(`/publication/${encodeURIComponent(w.publicationId)}`)}
                >
                  {labels.openDetail} <LandingIcon name="chevronRight" size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {isEmpty ? (
        <button type="button" className="qxr__cta qxr__cta--block" onClick={() => onNavigate('/')}>
          <LandingIcon name="bag" size={15} /> QX <LandingIcon name="chevronRight" size={12} />
        </button>
      ) : null}
    </div>
  );
}
